import React, { useEffect, useState } from 'react'
import {
  Button,
  TextField,
  Switch,
  FormControlLabel,
  InputAdornment,
} from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faAdd, faTrash, faSave } from '@fortawesome/free-solid-svg-icons'
import {
  useGetRouletteOptionsQuery,
  useCreateRouletteOptionMutation,
  useDeleteRouletteOptionMutation,
  useUpdateRouletteOptionMutation,
} from '../../../../store/api/rouletteApi'
import { IRouletteOption } from '../../../../types/roulette'
import { useUpdateRouletteStatusMutation } from '../../../../store/api/generalInfoApi'
import './RoulettePage.scss'
import { getStorageKey } from '../../../../utils/appType'

type RouletteOptionDraft = Omit<IRouletteOption, 'weight'> & { weight: string }

const toNumber = (value: string) => Number(value.replace(',', '.'))

const RoulettePage: React.FC = () => {
  const { data: rouletteOptionsData } = useGetRouletteOptionsQuery()
  const [createRouletteOption] = useCreateRouletteOptionMutation()
  const [deleteRouletteOptionMut] = useDeleteRouletteOptionMutation()
  const [updateRouletteOptionMut] = useUpdateRouletteOptionMutation()
  const [updateRouletteStatus] = useUpdateRouletteStatusMutation()

  const [rouletteOptions, setRouletteOptions] = useState<RouletteOptionDraft[]>([])
  const [isRouletteActive, setIsRouletteActive] = useState<boolean>(false)
  const [rouletteMinPrice, setRouletteMinPrice] = useState('0')
  const [originalMinPrice, setOriginalMinPrice] = useState('0')

  useEffect(() => {
    if (!rouletteOptionsData) return
    setRouletteOptions(
      rouletteOptionsData.map(option => ({
        ...option,
        weight: String(option.weight ?? 0),
      })),
    )
  }, [rouletteOptionsData])

  useEffect(() => {
    try {
      const info = JSON.parse(
        localStorage.getItem(getStorageKey('info')) || '{}',
      )

      if (info) {
        if (info.isRouletteActive !== undefined) {
          setIsRouletteActive(info.isRouletteActive)
        }

        if (info.rouletteMinPrice !== undefined) {
          setRouletteMinPrice(String(info.rouletteMinPrice))
          setOriginalMinPrice(String(info.rouletteMinPrice))
        }
      }
    } catch (error) {
      console.error('Error fetching general info:', error)
    }
  }, [])

  const addRouletteOption = () => {
    const newOption: RouletteOptionDraft = {
      option: '',
      style: { backgroundColor: '#ffffff', textColor: '#000000' },
      weight: '0',
    }

    setRouletteOptions([...rouletteOptions, newOption])
  }

  const removeRouletteOption = async (index: number) => {
    const option = rouletteOptions[index]

    if (option?._id) {
      try {
        await deleteRouletteOptionMut(option._id).unwrap()
      } catch (error) {
        console.error('Error deleting roulette option:', error)

        return
      }
    }

    const updatedOptions = rouletteOptions.filter((_, i) => i !== index)

    setRouletteOptions(updatedOptions)
  }

  const handleRouletteChange = (
    index: number,
    field: string,
    value: string | number,
  ) => {
    const updatedOptions = [...rouletteOptions]
    const option = updatedOptions[index]

    if (!option) {
      return
    }

    if (field.startsWith('style.')) {
      const styleField = field.split('.')[1] as keyof IRouletteOption['style']

      updatedOptions[index] = {
        ...option,
        style: {
          ...option.style,
          [styleField]: value,
        },
      }
    } else {
      updatedOptions[index] = {
        ...option,
        [field as keyof IRouletteOption]: value,
      }
    }

    setRouletteOptions(updatedOptions)
  }

  const handleRouletteToggle = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newValue = event.target.checked

    setIsRouletteActive(newValue)

    try {
      await updateRouletteStatus({
        isRouletteActive: newValue,
        rouletteMinPrice: toNumber(rouletteMinPrice),
      }).unwrap()

      // Update localStorage if it's used in your app
      const info = localStorage.getItem(getStorageKey('info'))

      if (info) {
        const parsedInfo = JSON.parse(info)

        parsedInfo.isRouletteActive = newValue
        parsedInfo.rouletteMinPrice = toNumber(rouletteMinPrice)
        localStorage.setItem(getStorageKey('info'), JSON.stringify(parsedInfo))
      }

      console.log(
        `Roulette feature ${newValue ? 'activated' : 'deactivated'} successfully`,
      )
      setOriginalMinPrice(rouletteMinPrice)
    } catch (error) {
      console.error('Error updating roulette status:', error)
      // Revert state if the API call fails
      setIsRouletteActive(!newValue)
    }
  }

  const handleMinPriceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRouletteMinPrice(event.target.value)
  }

  const saveRouletteMinPrice = async () => {
    try {
      await updateRouletteStatus({
        isRouletteActive,
        rouletteMinPrice: toNumber(rouletteMinPrice),
      }).unwrap()

      // Update localStorage if it's used in your app
      const info = localStorage.getItem(getStorageKey('info'))

      if (info) {
        const parsedInfo = JSON.parse(info)

        parsedInfo.rouletteMinPrice = toNumber(rouletteMinPrice)
        localStorage.setItem(getStorageKey('info'), JSON.stringify(parsedInfo))
      }

      setOriginalMinPrice(rouletteMinPrice)
    } catch (error) {
      console.error('Error updating minimum price:', error)
      setRouletteMinPrice(originalMinPrice)
    }
  }

  const handleSave = async () => {
    try {
      for (const option of rouletteOptions) {
        const payload = {
          option: option.option || '',
          style: option.style || {
            backgroundColor: '#ffffff',
            textColor: '#000000',
          },
          weight: toNumber(option.weight),
        }

        if (option._id) {
          await updateRouletteOptionMut({
            params: payload,
            id: option._id,
          }).unwrap()
        } else {
          const response = await createRouletteOption(payload).unwrap()

          option._id = response._id
        }
      }

      setRouletteOptions([...rouletteOptions])
      console.log('All changes saved successfully')
    } catch (error) {
      console.error('Error saving roulette options:', error)
    }
  }

  const calculateTotalWeight = () => {
    return rouletteOptions.reduce(
      (sum, option) => sum + (toNumber(option.weight) || 0),
      0,
    )
  }

  return (
    <div className='roulette-page'>
      {/* Header */}
      <div className='roulette-page__header'>
        <div className='roulette-page__header-title'>Ruletă</div>
        <Button
          variant='contained'
          startIcon={<FontAwesomeIcon icon={faSave} />}
          onClick={handleSave}
          sx={{
            backgroundColor: 'var(--color-primary)',
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: '8px',
            '&:hover': { backgroundColor: 'var(--color-primary-hover)' },
          }}
        >
          Salvează Tot
        </Button>
      </div>

      {/* Settings card */}
      <div className='roulette-page__settings'>
        <div className='roulette-page__settings-title'>Setări Ruletă</div>
        <div className='roulette-page__settings-row'>
          <div className='roulette-page__settings-row-toggle'>
            <FormControlLabel
              control={
                <Switch
                  checked={isRouletteActive}
                  onChange={handleRouletteToggle}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: 'var(--color-primary)' },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: 'var(--color-primary)',
                    },
                  }}
                />
              }
              label={isRouletteActive ? 'Activă' : 'Inactivă'}
            />
          </div>
          <div className='roulette-page__settings-row-price'>
            <TextField
              label='Preț minim (lei)'
              type='text'
              value={rouletteMinPrice}
              onChange={handleMinPriceChange}
              variant='outlined'
              size='small'
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>Lei</InputAdornment>
                ),
                inputProps: { inputMode: 'decimal' },
              }}
            />
            <Button
              variant='contained'
              size='small'
              startIcon={<FontAwesomeIcon icon={faSave} />}
              onClick={saveRouletteMinPrice}
              disabled={rouletteMinPrice === originalMinPrice}
              sx={{
                backgroundColor: 'var(--color-primary)',
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: '8px',
                whiteSpace: 'nowrap',
                '&:hover': { backgroundColor: 'var(--color-primary-hover)' },
                '&.Mui-disabled': { backgroundColor: '#eee', color: '#aaa' },
              }}
            >
              Salvează preț
            </Button>
          </div>
        </div>
        <div className='roulette-page__settings-hint'>
          {isRouletteActive
            ? `Clienții vor vedea roata când plasează comenzi de minim ${rouletteMinPrice} Lei și pot câștiga premii.`
            : 'Ruleta este dezactivată. Clienții vor plasa comenzi fără a vedea roata.'}
        </div>
      </div>

      {/* Weight summary */}
      <div className='roulette-page__weight'>
        <span>Greutate Totală: {calculateTotalWeight().toFixed(1)}%</span>
        {calculateTotalWeight() > 100 && (
          <span className='roulette-page__weight-error' role='status'>
            Greutatea totală depășește 100%!
          </span>
        )}
      </div>

      {/* Options list */}
      <div className='roulette-page__section-title'>Opțiuni Ruletă</div>
      <div className='roulette-page__options'>
        {rouletteOptions.map((option, index) => (
          <div
            key={option?._id || `new-${index}`}
            className='roulette-page__option'
            style={{
              borderLeftColor: option?.style?.backgroundColor || '#ddd',
            }}
          >
            {/* Name */}
            <div className='roulette-page__option-name'>
              <TextField
                label='Nume Opțiune'
                value={option?.option || ''}
                onChange={(e) =>
                  handleRouletteChange(index, 'option', e.target.value)
                }
                fullWidth
                variant='outlined'
                size='small'
              />
            </div>

            {/* Background color */}
            <div className='roulette-page__option-color'>
              <span className='roulette-page__option-color-label'>Fundal</span>
              <div className='roulette-page__option-color-picker'>
                <input
                  type='color'
                  value={option?.style?.backgroundColor || '#ffffff'}
                  onChange={(e) =>
                    handleRouletteChange(
                      index,
                      'style.backgroundColor',
                      e.target.value,
                    )
                  }
                />
              </div>
            </div>

            {/* Text color */}
            <div className='roulette-page__option-color'>
              <span className='roulette-page__option-color-label'>Text</span>
              <div className='roulette-page__option-color-picker'>
                <input
                  type='color'
                  value={option?.style?.textColor || '#000000'}
                  onChange={(e) =>
                    handleRouletteChange(
                      index,
                      'style.textColor',
                      e.target.value,
                    )
                  }
                />
              </div>
            </div>

            {/* Weight */}
            <div className='roulette-page__option-weight'>
              <TextField
                label='Șansa (%)'
                type='text'
                value={option.weight}
                onChange={(e) =>
                  handleRouletteChange(index, 'weight', e.target.value)
                }
                fullWidth
                variant='outlined'
                size='small'
                inputProps={{ inputMode: 'decimal' }}
              />
            </div>

            {/* Delete */}
            <button
              className='roulette-page__option-delete'
              onClick={() => removeRouletteOption(index)}
              disabled={!option?._id}
              title='Șterge'
            >
              <FontAwesomeIcon icon={faTrash} />
            </button>
          </div>
        ))}
      </div>

      {/* Footer actions */}
      <div className='roulette-page__actions'>
        <Button
          variant='outlined'
          startIcon={<FontAwesomeIcon icon={faAdd} />}
          onClick={addRouletteOption}
          sx={{
            borderColor: 'var(--color-primary)',
            color: 'var(--color-primary)',
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: '8px',
            '&:hover': {
              borderColor: 'var(--color-primary-hover)',
              background: 'rgba(255,22,71,0.05)',
            },
          }}
        >
          Adaugă Opțiune
        </Button>
      </div>
    </div>
  )
}

export default RoulettePage
